import { Group, Line } from 'react-konva'
import { KonvaEventObject } from 'konva/lib/Node'
import { Room2D } from '../../../models/Construction'
import { useCallback, useState } from 'react'
import { BaseComponentProps } from './EditorWrapper'
import { ElementConverter } from './ElementConverter'
import { ResizeMode } from './Elements'

interface RoomComponentProps extends BaseComponentProps<Room2D> {}

export const RoomComponent: React.FC<RoomComponentProps> = ({
  element,
  scale,
  thicknessMultiplier = 1,
  isSelected = false,
  onSelect,
  onResizeStart,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const points = ElementConverter.polygonToPoints(
    element.floor.poly_points,
    scale,
  )
  const centerX =
    (element.floor.poly_points.reduce((sum, p) => sum + p[0], 0) /
      element.floor.poly_points.length) *
    scale
  const centerY =
    (element.floor.poly_points.reduce((sum, p) => sum + p[1], 0) /
      element.floor.poly_points.length) *
    scale

  // Calculate bounding box for resize handles
  const minX = Math.min(...element.floor.poly_points.map((p) => p[0])) * scale
  const maxX = Math.max(...element.floor.poly_points.map((p) => p[0])) * scale
  const minY = Math.min(...element.floor.poly_points.map((p) => p[1])) * scale
  const maxY = Math.max(...element.floor.poly_points.map((p) => p[1])) * scale
  const width = maxX - minX
  const height = maxY - minY

  const handleClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true
      onSelect?.(element)
    },
    [element, onSelect],
  )

  const handleResizeStart = useCallback(
    (mode: ResizeMode, e: KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true
      const pos = e.target.getStage()?.getPointerPosition()
      if (pos && onResizeStart) {
        onResizeStart(
          element.id,
          'room',
          mode,
          { x: pos.x, y: pos.y },
          {
            centerX: centerX / scale,
            centerY: centerY / scale,
            width: width / scale,
            height: height / scale,
            angle: 0,
          },
        )
      }
    },
    [element, onResizeStart, centerX, centerY, width, height, scale],
  )

  const getRoomColor = (type: Room2D['type']): string => {
    const colors: Record<Room2D['type'], string> = {
      LIVING_ROOM: '#F0F8FF',
      CLOSET: '#FFF8DC',
      LINEN: '#F5F5DC',
      UNKNOWN: '#F5F5F5',
    }
    return colors[type]
  }

  return (
    <Group>
      <Line
        points={[...points, points[0], points[1]]}
        fill={
          isSelected
            ? '#E6F3FF'
            : isHovered
              ? '#E0E0E0'
              : getRoomColor(element.type)
        }
        stroke={isSelected ? '#FF0000' : isHovered ? '#999999' : '#CCCCCC'}
        strokeWidth={isSelected ? 2 : isHovered ? 2 : 1}
        closed={true}
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
    </Group>
  )
}
